import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROUTES } from '@shared/api';
import type {
  AiBacktestRow,
  AiFeedback,
  AiModelRegistry,
  AiSnapshot,
  Alarm,
  AlarmListResponse,
  AuditChainStatus,
  AuthStatus,
  AuthUser,
  CalibrationProposal,
  CockpitResponse,
  DataHealthStatus,
  EquipmentListResponse,
  EquipmentRecord,
  ESignature,
  ESignRequest,
  ESignVerifyResult,
  ExportJob,
  FeedPrice,
  HangUnit,
  HistorySeries,
  LabEntry,
  LabInput,
  LabListResponse,
  LoginResponse,
  MobilePreview,
  MonthlyCost,
  NotificationLog,
  ProductionBatch,
  ProductionEvent,
  ProductionOverview,
  QcResult,
  Recipe,
  RecipeTransition,
  SubsystemSnapshot,
  SuppressionWindow,
  UtilitySnapshot,
  UserSummary,
  ValveCard,
  WorkOrder,
  WorkOrderEvent,
  WorkOrderStatus,
} from '@shared/domain';
import { api } from './api';

/** Default live poll — matches the reference "刷新 ≤5s / 接口数据". */
const LIVE = { refetchInterval: 5000 } as const;

export const keys = {
  cockpit: ['cockpit'],
  telemetry: ['telemetry'],
  hang: ['hang'],
  makeup: ['makeup'],
  utilities: ['utilities'],
  production: ['production'],
  costPrices: ['cost', 'prices'],
  costMonthly: ['cost', 'monthly'],
  ai: ['ai'],
  aiBacktest: ['ai', 'backtest'],
  aiModels: ['ai', 'models'],
  aiFeedback: ['ai', 'feedback'],
  mobile: ['mobile'],
  alarms: ['alarms'],
  alarmHistory: ['alarms', 'history'],
  notifications: ['notifications'],
  equipment: ['equipment'],
  lab: ['lab'],
  recipes: ['recipes'],
  exports: ['exports'],
  esign: ['esign'],
  esignPk: ['esign', 'public-key'],
  auditVerify: ['audit', 'verify'],
  authStatus: ['auth', 'status'],
} as const;

/* ------------------------------ queries ------------------------------ */

export const useAuthStatus = () =>
  useQuery({ queryKey: keys.authStatus, queryFn: () => api<AuthStatus>(ROUTES.auth.status), staleTime: 60_000 });

export const useCockpit = () => useQuery({ queryKey: keys.cockpit, queryFn: () => api<CockpitResponse>(ROUTES.cockpit), ...LIVE });
export const useTelemetry = () => useQuery({ queryKey: keys.telemetry, queryFn: () => api<SubsystemSnapshot[]>(ROUTES.telemetry), ...LIVE });
// A2: data-source health (freshness of newest collected value).
export const useDataHealth = () =>
  useQuery({ queryKey: ['data-health'], queryFn: () => api<DataHealthStatus>(ROUTES.dataHealth), refetchInterval: 10_000 });
export const useHistory = (keysParam: string, points = 120) =>
  useQuery({
    queryKey: ['history', keysParam, points],
    queryFn: () => api<HistorySeries[]>(`${ROUTES.telemetryHistory}?keys=${encodeURIComponent(keysParam)}&points=${points}`),
    ...LIVE,
  });
export const useHang = () => useQuery({ queryKey: keys.hang, queryFn: () => api<HangUnit[]>(ROUTES.hang), ...LIVE });
export const useMakeup = () => useQuery({ queryKey: keys.makeup, queryFn: () => api<ValveCard[]>(ROUTES.makeup), ...LIVE });
export const useUtilities = () => useQuery({ queryKey: keys.utilities, queryFn: () => api<UtilitySnapshot>(ROUTES.utilities), ...LIVE });
export const useProduction = () => useQuery({ queryKey: keys.production, queryFn: () => api<ProductionOverview>(ROUTES.production), ...LIVE });
export const useAi = () => useQuery({ queryKey: keys.ai, queryFn: () => api<AiSnapshot>(ROUTES.ai), ...LIVE });
// Backtest walks the full history per key; refresh gently (60s) rather than live-polling.
export const useAiBacktest = () =>
  useQuery({ queryKey: keys.aiBacktest, queryFn: () => api<AiBacktestRow[]>(ROUTES.aiBacktest), staleTime: 60_000, refetchInterval: 60_000 });
export const useAiModels = () => useQuery({ queryKey: keys.aiModels, queryFn: () => api<AiModelRegistry>(ROUTES.aiModels), staleTime: 300_000 });
export const useAiFeedback = () => useQuery({ queryKey: keys.aiFeedback, queryFn: () => api<AiFeedback[]>(ROUTES.aiFeedback), ...LIVE });
export const useSubmitAiFeedback = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { action: 'accept' | 'reject'; model: string; modelVersion: string; suggestedFeedKg: number; suggestedDo1h: number; anomalyRiskPct: number; actualFeedKg?: number; note?: string }) => api<AiFeedback>(ROUTES.aiFeedback, { method: 'POST', body }),
    onSuccess: () => invalidate(keys.aiFeedback),
  });
};
export const useMobile = () => useQuery({ queryKey: keys.mobile, queryFn: () => api<MobilePreview>(ROUTES.mobile), ...LIVE });

export const useAlarms = () => useQuery({ queryKey: keys.alarms, queryFn: () => api<AlarmListResponse>(ROUTES.alarms.list), ...LIVE });
export const useAlarmHistory = () => useQuery({ queryKey: keys.alarmHistory, queryFn: () => api<Alarm[]>(ROUTES.alarms.history) });
export const useNotifications = () =>
  useQuery({ queryKey: keys.notifications, queryFn: () => api<NotificationLog[]>(ROUTES.alarms.notifications), ...LIVE });

export const useEquipment = (params?: { lifecycle?: string; search?: string }) =>
  useQuery({
    queryKey: [...keys.equipment, params],
    queryFn: () => {
      const q = new URLSearchParams();
      if (params?.lifecycle) q.set('lifecycle', params.lifecycle);
      if (params?.search) q.set('search', params.search);
      const qs = q.toString();
      return api<EquipmentListResponse>(ROUTES.equipment.list + (qs ? `?${qs}` : ''));
    },
  });

export const useEquipmentDetail = (slug: string) =>
  useQuery({
    queryKey: ['equipment', slug],
    enabled: !!slug,
    queryFn: () => api<{ record: EquipmentRecord; snapshot: SubsystemSnapshot | null }>(ROUTES.equipment.detail(slug)),
  });

export const useLabEntries = () => useQuery({ queryKey: keys.lab, queryFn: () => api<LabListResponse>(ROUTES.lab.list) });
export const useLabQc = (metric: 'ph' | 'nh3n' | 'no2') =>
  useQuery({ queryKey: ['lab', 'qc', metric], queryFn: () => api<QcResult>(`${ROUTES.lab.qc}?metric=${metric}`) });
export const useLabChain = (id: string | null) =>
  useQuery({ queryKey: ['lab', 'chain', id], enabled: !!id, queryFn: () => api<LabEntry[]>(ROUTES.lab.chain(id as string)) });

export const useRecipes = () => useQuery({ queryKey: keys.recipes, queryFn: () => api<Recipe[]>(ROUTES.recipes.list), ...LIVE });
export const useRecipeTransitions = (id: string | null) =>
  useQuery({ queryKey: ['recipes', 'transitions', id], enabled: !!id, queryFn: () => api<RecipeTransition[]>(ROUTES.recipes.transitions(id as string)) });
export const useRecipeTransition = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; action: string; signatureId?: string; ifMatch: string }) =>
      api<{ recipe: Recipe }>(ROUTES.recipes.transition(v.id), { method: 'POST', body: { action: v.action, signatureId: v.signatureId }, headers: { 'if-match': v.ifMatch } }),
    onSuccess: (_d, v) => invalidate(keys.recipes, ['recipes', 'transitions', v.id]),
  });
};
export const useExportJobs = () => useQuery({ queryKey: keys.exports, queryFn: () => api<ExportJob[]>(ROUTES.exports.list), ...LIVE });

export const useESignatures = () => useQuery({ queryKey: keys.esign, queryFn: () => api<ESignature[]>(ROUTES.esign.list) });
// Passive tamper tripwire: re-derives the audit hash-chain server-side ~30s cadence.
export const useAuditVerify = (enabled = true) =>
  useQuery({ queryKey: keys.auditVerify, enabled, queryFn: () => api<AuditChainStatus>(ROUTES.auditVerify), refetchInterval: 30_000 });
export const useESignPublicKey = () =>
  useQuery({ queryKey: keys.esignPk, queryFn: () => api<{ algorithm: string; ephemeral: boolean; publicKeyPem: string }>(ROUTES.esign.publicKey), staleTime: 300_000 });
export const useESignVerify = (id: string | null) =>
  useQuery({ queryKey: ['esign', 'verify', id], enabled: !!id, queryFn: () => api<ESignVerifyResult>(ROUTES.esign.verify(id as string)) });

/* ------------------------------ mutations ---------------------------- */

function useInvalidate() {
  const qc = useQueryClient();
  return (...groups: (readonly string[])[]) => groups.forEach((g) => qc.invalidateQueries({ queryKey: g as string[] }));
}

export const useLogin = () =>
  useMutation({ mutationFn: (body: { username: string; password: string }) => api<LoginResponse>(ROUTES.auth.login, { method: 'POST', body }) });

/** Self-service password change. Returns a freshly-signed token + updated user so the
 *  session's mustChangePassword clears immediately (server re-signs after token_version bump). */
export const useChangePassword = () =>
  useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api<{ token: string; user: AuthUser; rotatedAt: string }>(ROUTES.auth.changePassword, { method: 'POST', body }),
  });

export const useAckAlarm = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; note?: string }) => api<Alarm>(ROUTES.alarms.ack(v.id), { method: 'POST', body: { note: v.note } }),
    onSuccess: () => invalidate(keys.alarms, keys.notifications, keys.cockpit),
  });
};

export const useShelveAlarm = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; minutes: number }) => api<Alarm>(ROUTES.alarms.shelve(v.id), { method: 'POST', body: { minutes: v.minutes } }),
    onSuccess: () => invalidate(keys.alarms, keys.cockpit),
  });
};

export const useUnshelveAlarm = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api<Alarm>(ROUTES.alarms.unshelve(id), { method: 'POST' }),
    onSuccess: () => invalidate(keys.alarms, keys.cockpit),
  });
};

export const useSuppressions = () => useQuery({ queryKey: ['suppress'], queryFn: () => api<SuppressionWindow[]>(ROUTES.alarms.suppressions) });
export const useCreateSuppression = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { system?: string; source?: string; level?: string; reason: string; from: string; to: string }) =>
      api<SuppressionWindow>(ROUTES.alarms.suppressions, { method: 'POST', body }),
    onSuccess: () => invalidate(['suppress'], keys.alarms),
  });
};
export const useDeleteSuppression = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api(ROUTES.alarms.suppression(id), { method: 'DELETE' }),
    onSuccess: () => invalidate(['suppress'], keys.alarms),
  });
};

export const useAlarmToWorkOrder = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api<Alarm>(ROUTES.alarms.toWorkOrder(id), { method: 'POST' }),
    onSuccess: () => invalidate(keys.alarms),
  });
};

export const useLabSubmit = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: LabInput) => api<{ entry: LabEntry; bypass: boolean }>(ROUTES.lab.submit, { method: 'POST', body }),
    onSuccess: () => invalidate(keys.lab, keys.cockpit, keys.alarms),
  });
};

export const useLabCorrect = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (vars: { id: string; body: LabInput & { reason: string; signatureId: string } }) =>
      api<{ entry: LabEntry }>(ROUTES.lab.correct(vars.id), { method: 'POST', body: vars.body }),
    onSuccess: () => invalidate(keys.lab),
  });
};

export const useCalibrate = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { entryId: string; metric: CalibrationProposal['metric']; channel: string; absoluteOffset: number; signatureId: string }) =>
      api<CalibrationProposal>(ROUTES.lab.calibrate, { method: 'POST', body }),
    onSuccess: () => invalidate(['calibrations']),
  });
};

export const useCreateExport = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { purpose: string; format: ExportJob['format']; dataset?: string }) => api<ExportJob>(ROUTES.exports.create, { method: 'POST', body }),
    onSuccess: () => invalidate(keys.exports),
  });
};

export const useExportLink = () =>
  useMutation({
    mutationFn: (id: string) => api<{ url: string; expiresAt: string }>(ROUTES.exports.link(id)),
  });

export const usePatchRecipe = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<Pick<Recipe, 'name' | 'status'>>; ifMatch: string }) =>
      api<Recipe>(ROUTES.recipes.detail(vars.id), {
        method: 'PATCH',
        body: vars.patch,
        headers: { 'if-match': vars.ifMatch },
      }),
    onSuccess: () => invalidate(keys.recipes),
  });
};

export const useValveMode = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; mode: 'auto' | 'manual' }) => api<{ id: string; mode: string }>(ROUTES.makeupValveMode(v.id), { method: 'POST', body: { mode: v.mode } }),
    onSuccess: () => invalidate(['makeup']),
  });
};

export const useValveCommand = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; open: boolean }) => api<{ id: string; open: boolean; ok: boolean }>(ROUTES.makeupValveCommand(v.id), { method: 'POST', body: { open: v.open } }),
    onSuccess: () => invalidate(['makeup', 'alarms']),
  });
};

export const useESignCreate = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ESignRequest) => api<ESignature>(ROUTES.esign.create, { method: 'POST', body }),
    onSuccess: () => invalidate(keys.esign),
  });
};

export const useUsers = () => useQuery({ queryKey: ['admin', 'users'], queryFn: () => api<UserSummary[]>(ROUTES.admin.users) });
export const useCreateUser = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { username: string; displayName: string; role: string; password: string }) => api<UserSummary>(ROUTES.admin.users, { method: 'POST', body }),
    onSuccess: () => invalidate(['admin', 'users']),
  });
};
export const usePatchUser = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; patch: { role?: string; active?: boolean } }) => api(ROUTES.admin.user(v.id), { method: 'PATCH', body: v.patch }),
    onSuccess: () => invalidate(['admin', 'users']),
  });
};
export const useResetUserPassword = () =>
  useMutation({
    mutationFn: (v: { id: string; password: string }) => api(ROUTES.admin.userPassword(v.id), { method: 'POST', body: { password: v.password } }),
  });

export const useBatches = () => useQuery({ queryKey: ['production', 'batches'], queryFn: () => api<ProductionBatch[]>(ROUTES.productionBatches) });
export const useBatchEvents = (id: string | null) =>
  useQuery({ queryKey: ['production', 'events', id], enabled: !!id, queryFn: () => api<ProductionEvent[]>(ROUTES.productionBatchEvents(id as string)) });
export const useCreateBatch = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { unit: string; species: string; count: number; avgWeightKg: number; stockedAt: string }) => api<ProductionBatch>(ROUTES.productionBatches, { method: 'POST', body }),
    onSuccess: () => invalidate(['production'], keys.production),
  });
};
export const useAppendBatchEvent = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; body: { type: string; qty: number; note?: string; toBatch?: string } }) => api<ProductionEvent>(ROUTES.productionBatchEvents(v.id), { method: 'POST', body: v.body }),
    onSuccess: (_d, v) => {
      invalidate(['production'], ['production', 'events', v.id], keys.production);
      if (v.body.toBatch) invalidate(['production', 'events', v.body.toBatch]);
    },
    onError: (e) => window.alert((e as Error)?.message ?? '操作被拒绝'),
  });
};

/* ---------------- C4 cost parameters (admin-managed) ---------------- */
export const useFeedPrices = () =>
  useQuery({ queryKey: keys.costPrices, queryFn: () => api<FeedPrice[]>(ROUTES.cost.prices), staleTime: 30_000 });
export const useAddFeedPrice = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { effectiveFrom: string; priceYuanPerKg: number; note?: string }) => api<FeedPrice>(ROUTES.cost.prices, { method: 'POST', body }),
    onSuccess: () => invalidate(keys.costPrices, keys.production),
    onError: (e) => window.alert((e as Error)?.message ?? '价格保存失败'),
  });
};
export const useDeleteFeedPrice = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) => api<{ ok: boolean }>(ROUTES.cost.price(id), { method: 'DELETE' }),
    onSuccess: () => invalidate(keys.costPrices, keys.production),
  });
};
export const useMonthlyCosts = () =>
  useQuery({ queryKey: keys.costMonthly, queryFn: () => api<MonthlyCost[]>(ROUTES.cost.monthly), staleTime: 30_000 });
export const useUpsertMonthlyCost = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { month: string; seedYuan: number; energyYuan: number; laborYuan: number; note?: string }) => api<MonthlyCost>(ROUTES.cost.monthly, { method: 'PUT', body }),
    onSuccess: () => invalidate(keys.costMonthly, keys.production),
    onError: (e) => window.alert((e as Error)?.message ?? '成本保存失败'),
  });
};
export const useDeleteMonthlyCost = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (month: string) => api<{ ok: boolean }>(ROUTES.cost.monthlyEntry(month), { method: 'DELETE' }),
    onSuccess: () => invalidate(keys.costMonthly, keys.production),
  });
};

const WO_KEY = ['work-orders'];
export const useWorkOrders = (status?: WorkOrderStatus, equipment?: string) =>
  useQuery({
    queryKey: [...WO_KEY, status, equipment],
    queryFn: () => {
      const p = new URLSearchParams();
      if (status) p.set('status', status);
      if (equipment) p.set('equipment', equipment);
      const qs = p.toString();
      return api<WorkOrder[]>(ROUTES.workOrders + (qs ? `?${qs}` : ''));
    },
    ...LIVE,
  });
export const useWorkOrder = (id: string | null) =>
  useQuery({ queryKey: [...WO_KEY, 'detail', id], enabled: !!id, queryFn: () => api<{ workOrder: WorkOrder; events: WorkOrderEvent[] }>(ROUTES.workOrder(id as string)) });
export const useCreateWorkOrder = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { title: string; description?: string; priority: string; assignee?: string; equipmentSlug?: string }) => api<WorkOrder>(ROUTES.workOrders, { method: 'POST', body }),
    onSuccess: () => invalidate(WO_KEY),
  });
};
export const useUpdateWorkOrder = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; patch: { title?: string; description?: string; priority?: string; assignee?: string; equipmentSlug?: string } }) => api<WorkOrder>(ROUTES.workOrder(v.id), { method: 'PATCH', body: v.patch }),
    onSuccess: (_d, v) => invalidate(WO_KEY, [...WO_KEY, 'detail', v.id]),
  });
};
export const useSetWorkOrderStatus = () => {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { id: string; status: WorkOrderStatus; note?: string }) => api<WorkOrder>(ROUTES.workOrderStatus(v.id), { method: 'PATCH', body: { status: v.status, note: v.note } }),
    onSuccess: (_d, v) => invalidate(WO_KEY, [...WO_KEY, 'detail', v.id]),
  });
};
